/**
 * Gemini 호출기 — Supabase Edge Function(erp-chat) 중계 방식
 *
 * 예전에는 브라우저가 API 키를 들고 Google 을 직접 불렀다. 그런데 CRA 는 REACT_APP_* 값을
 * 빌드 결과물에 그대로 박아 넣기 때문에, 개발자도구 네트워크 탭이나 main.js 를 열면 키가
 * 그대로 보였다. 그래서 키를 서버(Supabase 시크릿)로 옮기고, 여기서는 우리 Edge Function 만 부른다.
 * 브라우저는 이제 Gemini 키를 아예 모른다.
 *
 * 키는 hanmaeum 과 공유한다 — 같은 Supabase 프로젝트라서 거기 이미 설정된 GEMINI_API_KEY
 * 시크릿이 그대로 쓰인다. (배포 방법은 supabase/functions/README.md)
 *
 * supabase-js 의 functions.invoke 대신 fetch 를 쓰는 이유: invoke 는 AbortSignal 을 받지 않는데,
 * 채팅에는 "중단" 버튼이 있어서 signal 이 꼭 필요하다.
 *
 * 필요한 .env 값 (전부 REACT_APP_ 접두사, 변경 후 dev 서버 재시작):
 *   REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY, REACT_APP_ERP_APP_TOKEN
 */

const FUNCTION_NAME = 'erp-chat';

const getSupabaseUrl = () => (process.env.REACT_APP_SUPABASE_URL || '').trim().replace(/\/$/, '');
const getAnonKey = () => (process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();
const getAppToken = () => (process.env.REACT_APP_ERP_APP_TOKEN || '').trim();

/** 중계 함수를 부를 준비가 됐는지 (셋 다 있어야 한다) */
export const isAiConfigured = () => !!(getSupabaseUrl() && getAnonKey() && getAppToken());

export class GeminiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'GeminiError';
    this.status = status;
  }
}

/**
 * 한 번 호출한다.
 *
 * ⚠ 역할·범위·안전 규칙은 여기서 보내지 않는다. 그 문장들은 Edge Function 이 들고 있다.
 *   브라우저가 규칙을 만들어 보내면 개발자도구에서 그 한 줄만 지우고 보내는 것으로 무력화되기
 *   때문이다(앱 토큰도 번들에 있어서 막지 못한다). 여기서 보내는 것은 규칙이 아니라 "상황"뿐이다.
 *
 * @param {object} p
 * @param {object} p.context   지금 상황 { today, user, route, screen, demo, toolGuide, capabilityHint }
 * @param {Array} p.contents   대화 기록 (role: user | model)
 * @param {Array} p.tools      [{ functionDeclarations: [...] }]
 * @param {AbortSignal} [p.signal]
 * @returns {Promise<{parts: Array, finishReason: string, usage: object|null, blocked: boolean}>}
 */
export async function generate({ context, contents, tools, signal }) {
  if (!isAiConfigured()) {
    throw new GeminiError(
      'AI 연결 설정이 없습니다. .env 에 REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY / REACT_APP_ERP_APP_TOKEN 를 넣고 개발 서버를 다시 시작하세요.',
    );
  }

  let response;
  try {
    response = await fetch(`${getSupabaseUrl()}/functions/v1/${FUNCTION_NAME}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Supabase 게이트웨이 통과용. 이 키는 원래 공개용(publishable)이라 노출돼도 되는 값이다.
        apikey: getAnonKey(),
        Authorization: `Bearer ${getAnonKey()}`,
        // 함수 자체 검사용 앱 토큰
        'x-erp-app-token': getAppToken(),
      },
      body: JSON.stringify({
        contents,
        ...(context ? { context } : {}),
        ...(tools ? { tools } : {}),
      }),
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new GeminiError(`AI 서버에 연결하지 못했습니다: ${error?.message || error}`);
  }

  const raw = await response.text();
  let json = null;
  try {
    json = raw ? JSON.parse(raw) : null;
  } catch (_) {}

  if (!response.ok || json?.ok === false) {
    const message = json?.error || raw?.slice(0, 400) || `HTTP ${response.status}`;
    throw new GeminiError(message, response.status);
  }

  const parts = json?.parts || [];
  const finishReason = json?.finishReason || '';

  if (!parts.length && finishReason && finishReason !== 'STOP') {
    throw new GeminiError(`AI 가 답변을 만들지 못했습니다 (사유: ${finishReason}).`);
  }

  // blocked: 주제 게이트가 막은 응답. parts 에 안내 문구가 들어 있으므로 화면은 평소처럼 그리면 된다.
  return { parts, finishReason, usage: json?.usage || null, blocked: json?.blocked === true };
}
